import { useMemo } from "react"
import { DateTime } from "luxon"
import { EstatusTarifa, Tarifa, TiposAlojamientos, TipoTarifa } from "src/gql/schema"
import { getResumenHotelHospedaje, getResumenMotelHospedaje, getResumenPersonasExtra } from "./helpers/resumen"
import { Desglose, DesgloseTarifa, GrupoTarifa } from "./useMotorTarifas.type"
import { getGruposTarifas } from "./helpers/motor"
import { getDesglose } from "./helpers/desglose"
import { useFormatDate } from "src/shared/hooks/useFormatDate"

type UseMotorParams = {
    tarifas: Tarifa[]
    fecha_inicio: string
    fecha_final: string
    personas_extra: number,
    horas_extra: number,
    hospedajes_extra: number
}

interface Params extends UseMotorParams {
    tarifas_adicionales: Tarifa[]
    tipo: string
}

interface DefaultTarifa extends Tarifa {
    tipo_habitacion: any
}

const DEFAULT_TARIFA: DefaultTarifa = {
    tarifa_id: "",
    hotel_id: "",
    hotel: {} as Tarifa["hotel"],
    nombre: "",
    eliminado: false,
    estatus: EstatusTarifa.Activa,
    duracion_renta: 0,
    costo_habitacion: 0,
    costo_persona_extra: 0,
    costo_early_checkin: 0,
    costo_hospedaje_extra: 0,
    costo_hora_extra: 0,
    tipo_habitacion_id: "",
    hora_checkin: "",
    hora_checkout: "",
    tipo_alojamiento: TiposAlojamientos.Hotel,
    tipo_tarifa: TipoTarifa.Renta,
    fecha_final: "",
    fecha_inicio: "",
    hora_final: "",
    hora_inicio: "",
    tipo_habitacion: {
        horas_extra_max: 0,
        hospedajes_extra_max: 0,
        personas_extra_max: 0
    },
    dias_disponibles: {
        lunes: 0,
        martes: 0,
        miercoles: 0,
        jueves: 0,
        viernes: 0,
        sabado: 0,
        domingo: 0,
    },
    grupo_tarifa_id: "",
    grupo_tarifa: null,
}

const EMPTY_GRUPOS: GrupoTarifa[] = []
const EMPTY_RESULT: Desglose = { desglose: [] as DesgloseTarifa[], total: 0, costo_early_checkin: 0, tarifa_primer_dia: DEFAULT_TARIFA, tarifa_ultimo_dia: DEFAULT_TARIFA }

function useMotor({ tarifas, fecha_inicio, fecha_final, personas_extra, horas_extra, hospedajes_extra }: UseMotorParams) {
    const { getTimezone } = useFormatDate()
    const zonehotel = getTimezone()

    // Normaliza las fechas entrantes a medianoche en zona del hotel, ignorando la parte de hora
    // para evitar que offsets UTC desplacen la fecha un día atrás.
    const fechaInicioHotel = useMemo(() => {
        if (!fecha_inicio) return null
        return DateTime.fromISO(fecha_inicio.substring(0, 10), { zone: zonehotel }).toJSDate()
    }, [fecha_inicio, zonehotel])

    const fechaFinalHotel = useMemo(() => {
        if (!fecha_final) return null
        return DateTime.fromISO(fecha_final.substring(0, 10), { zone: zonehotel }).toJSDate()
    }, [fecha_final, zonehotel])

    const grupos = useMemo(() => {
        if (tarifas.length > 0 && fechaInicioHotel && fechaFinalHotel) {
            return getGruposTarifas(tarifas, fechaInicioHotel, fechaFinalHotel, zonehotel)
        }

        return EMPTY_GRUPOS
    }, [tarifas, fechaInicioHotel, fechaFinalHotel, zonehotel])

    const result: Desglose = useMemo(() => {
        if (grupos.length === 0) return EMPTY_RESULT

        const fecha_inicio_iso = fechaInicioHotel?.toISOString() ?? fecha_inicio
        const fecha_fin_iso = fechaFinalHotel?.toISOString() ?? fecha_final
        const desglose = getDesglose({ grupos, personas_extra, horas_extra, hospedajes_extra, fecha_inicio: fecha_inicio_iso, fecha_fin: fecha_fin_iso, zonehotel })
        return desglose

    }, [grupos, personas_extra, horas_extra, hospedajes_extra, zonehotel])

    return result
}

export function useMotorTarifas({ tipo, tarifas, tarifas_adicionales, horas_extra, personas_extra, hospedajes_extra, fecha_inicio, fecha_final }: Params) {
    const tarifas_finales = useMemo(() => {
        return tipo === TiposAlojamientos.Motel ? tarifas : [...tarifas, ...tarifas_adicionales]
    }, [tipo, tarifas, tarifas_adicionales])


    const { desglose, total, costo_early_checkin, tarifa_primer_dia, tarifa_ultimo_dia } = useMotor({
        tarifas: tarifas_finales,
        fecha_inicio,
        fecha_final,
        horas_extra,
        personas_extra,
        hospedajes_extra,
    })

    const resumen = useMemo(() => {
        return {
            resumen_tarifas: {
                hospedaje: desglose.length > 0 ? tipo === TiposAlojamientos.Motel ? getResumenMotelHospedaje(desglose) : getResumenHotelHospedaje(desglose) : [],
                personas_extra: desglose.length > 0 && personas_extra > 0 ? getResumenPersonasExtra(desglose) : []
            },
            total_tarifas: total,
            desglose_tarifas: desglose,
            costo_early_checkin,
            tarifa_primer_dia: tarifa_primer_dia as Tarifa,
            tarifa_ultimo_dia: tarifa_ultimo_dia as Tarifa
        }
    }, [desglose, tipo])

    return resumen
}